// 性能监控工具
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map()
    this.observers = []
    this.setupObservers()
  }

  // 设置性能观察者
  setupObservers() {
    // 观察页面加载性能
    if ('PerformanceObserver' in window) {
      // 观察导航性能
      const navObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordNavigation(entry)
        }
      })
      navObserver.observe({ entryTypes: ['navigation'] })
      this.observers.push(navObserver)

      // 观察资源加载性能
      const resourceObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordResource(entry)
        }
      })
      resourceObserver.observe({ entryTypes: ['resource'] })
      this.observers.push(resourceObserver)

      // 观察长任务
      const longTaskObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.recordLongTask(entry)
        }
      })
      longTaskObserver.observe({ entryTypes: ['longtask'] })
      this.observers.push(longTaskObserver)
    }
  }

  // 记录导航性能
  recordNavigation(entry) {
    const metrics = {
      // DNS查询时间
      dnsTime: entry.domainLookupEnd - entry.domainLookupStart,
      // TCP连接时间
      tcpTime: entry.connectEnd - entry.connectStart,
      // SSL握手时间
      sslTime: entry.secureConnectionStart > 0 ? entry.connectEnd - entry.secureConnectionStart : 0,
      // 请求时间
      requestTime: entry.responseStart - entry.requestStart,
      // 响应时间
      responseTime: entry.responseEnd - entry.responseStart,
      // DOM解析时间
      domParseTime: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
      // 资源加载时间
      resourceLoadTime: entry.loadEventEnd - entry.loadEventStart,
      // 首次内容绘制
      fcp: this.getFCP(),
      // 最大内容绘制
      lcp: this.getLCP(),
      // 首次输入延迟
      fid: this.getFID(),
      // 累积布局偏移
      cls: this.getCLS()
    }

    this.metrics.set('navigation', metrics)
    this.reportMetrics('navigation', metrics)
  }

  // 记录资源性能
  recordResource(entry) {
    if (entry.duration > 1000) { // 只记录耗时超过1秒的资源
      const resourceMetrics = {
        name: entry.name,
        type: entry.initiatorType,
        duration: entry.duration,
        size: entry.transferSize,
        startTime: entry.startTime
      }

      this.reportMetrics('resource', resourceMetrics)
    }
  }

  // 记录长任务
  recordLongTask(entry) {
    const longTaskMetrics = {
      duration: entry.duration,
      startTime: entry.startTime,
      name: entry.name
    }

    this.reportMetrics('longtask', longTaskMetrics)
  }

  // 获取首次内容绘制时间
  getFCP() {
    const fcpEntry = performance.getEntriesByName('first-contentful-paint')[0]
    return fcpEntry ? fcpEntry.startTime : 0
  }

  // 获取最大内容绘制时间
  getLCP() {
    return new Promise((resolve) => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1]
          resolve(lastEntry.startTime)
        })
        observer.observe({ entryTypes: ['largest-contentful-paint'] })
      } else {
        resolve(0)
      }
    })
  }

  // 获取首次输入延迟
  getFID() {
    return new Promise((resolve) => {
      if ('PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          const firstEntry = list.getEntries()[0]
          resolve(firstEntry.processingStart - firstEntry.startTime)
        })
        observer.observe({ entryTypes: ['first-input'] })
      } else {
        resolve(0)
      }
    })
  }

  // 获取累积布局偏移
  getCLS() {
    return new Promise((resolve) => {
      if ('PerformanceObserver' in window) {
        let clsValue = 0
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value
            }
          }
          resolve(clsValue)
        })
        observer.observe({ entryTypes: ['layout-shift'] })
      } else {
        resolve(0)
      }
    })
  }

  // 手动记录性能指标
  mark(name) {
    if (performance.mark) {
      performance.mark(name)
    }
  }

  // 测量性能
  measure(name, startMark, endMark) {
    if (performance.measure) {
      performance.measure(name, startMark, endMark)
      const measures = performance.getEntriesByName(name)
      const latestMeasure = measures[measures.length - 1]
      
      this.reportMetrics('custom', {
        name,
        duration: latestMeasure.duration,
        startTime: latestMeasure.startTime
      })
    }
  }

  // 记录API请求性能
  recordAPICall(url, method, duration, status) {
    const apiMetrics = {
      url,
      method,
      duration,
      status,
      timestamp: Date.now()
    }

    this.reportMetrics('api', apiMetrics)
  }

  // 记录页面性能
  recordPagePerformance(pageName) {
    const pageMetrics = {
      pageName,
      loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart,
      domReadyTime: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
      firstPaintTime: this.getFCP(),
      timestamp: Date.now()
    }

    this.reportMetrics('page', pageMetrics)
  }

  // 获取内存使用情况
  getMemoryUsage() {
    if (performance.memory) {
      return {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      }
    }
    return null
  }

  // 报告性能指标
  reportMetrics(type, metrics) {
    // 开发环境打印性能指标 - 已禁用以减少控制台日志
    // if (process.env.NODE_ENV === 'development') {
    //   console.group(`📊 Performance Metrics - ${type}`)
    //   console.table(metrics)
    //   console.groupEnd()
    // }

    // 生产环境可以发送到性能监控服务
    if (process.env.NODE_ENV === 'production') {
      // 这里可以集成性能监控服务
      this.sendToAnalytics(type, metrics)
    }
  }

  // 发送到分析服务
  sendToAnalytics(type, metrics) {
    // 示例：发送到Google Analytics或其他分析服务
    // gtag('event', 'performance', {
    //   event_category: type,
    //   event_label: JSON.stringify(metrics)
    // })
  }

  // 清理观察者
  cleanup() {
    this.observers.forEach(observer => observer.disconnect())
    this.observers = []
  }
}

// 创建全局性能监控实例
export const performanceMonitor = new PerformanceMonitor()

// Vue插件
export default {
  install(Vue) {
    Vue.prototype.$performance = performanceMonitor
    
    // 在路由切换时记录性能
    Vue.mixin({
      beforeRouteEnter(to, from, next) {
        performanceMonitor.mark(`route-${to.name}-start`)
        next()
      },
      beforeRouteLeave(to, from, next) {
        performanceMonitor.mark(`route-${from.name}-end`)
        performanceMonitor.measure(
          `route-${from.name}-duration`,
          `route-${from.name}-start`,
          `route-${from.name}-end`
        )
        next()
      }
    })
  }
} 